<template>
  <ion-app>
    <BiometricModal
        :visible="state.isOpen"
        :title="state.title"
        :message="state.message"
        :loading="state.isLoading"
        :error="state.error"
        @confirm="handleConfirm"
        @cancel="handleCancel"
    />

    <template v-if="isAppReady">
      <navbar :onToggleDrawer="toggleDrawer"/>
      <ion-router-outlet/>
      <suite-drawer :is-drawer-open="isDrawerOpen" @close="closeDrawer" />
    </template>

    <div v-else class="loading-center">
      <div class="loader-content">
        <ion-spinner name="crescent" class="custom-spinner"></ion-spinner>
        <p>Syncing Session...</p>
      </div>
    </div>
  </ion-app>
</template>

<script setup lang="ts">
import { App } from "@capacitor/app";
import { IonApp, IonRouterOutlet, IonSpinner } from '@ionic/vue';
import { onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAuth } from '@/composables/useAuth';
import { useNats } from '@/composables/useNats';
import { useNotifications } from '@/composables/useNotifications';
import { useNavigation } from '@/composables/useNavigation';
import { useHeartBeatTracker } from "@/composables/useHeartBeatTracker.ts";

import { useSettingsStore } from '@/stores/useSettingsStore';
import { useServiceStore } from "@/stores/useServiceStore.ts";

import { USER_ACTIVITY_EVENTS } from '@/utils/constants';
import { connectNatsWithRetry, natsSubscribe } from '@/services/NatsService.ts';
import { processPendingNotification } from '@/services/NotificationHandler';
import { IframeBridge } from "@/services/IframeBridge/IframeBridgeService.ts";
import { GeoLocationService } from "@/services/GeoLocationService.ts";
import { StorageService } from "@/services/storageService.ts";
import { markAuthReady } from '@/router';
import { getBaseDomain, isNative } from "@/utils/platform.ts";
import { SessionHub } from '@/utils/sessionHub';

import BiometricModal from '@/components/BiometricModal.vue';
import Navbar from "@/components/Navbar.vue";
import SuiteDrawer from "@/components/SuiteDrawer.vue";
import SessionModal from "@/components/modals/SessionModal.vue";

import { useLocale } from "@/composables/useLocale.ts";
import { useBiometric } from "@/composables/useBiometric.ts";
import { showModal, closeSessionModal } from "@/utils/notifications.ts";
import { isValidHeilaMessage, validateOrigin, validateHandshakeNonce } from "@/utils/security/messaging";
import SendIframe from "@/services/IframeBridge/iframes/delivery/outbound.ts";

const { state, handleConfirm, handleCancel } = useBiometric();

const route = useRoute();
const { initLocale } = useLocale();

const {
  initAuth,
  user,
  isLoggedIn,
  showSessionModal,
  timeLeft,
  modalType,
  getAttendanceStatus
} = useAuth();

const { activityActions, manageTracker, setConnection } = useNats();
const { getQueryParam } = useNavigation();
const settingsStore = useSettingsStore();
const serviceStore = useServiceStore();
const { setupNotifications, sendTokenIfReady } = useNotifications();
const { startPostMessageTracker, stopPostMessageTracker } = useHeartBeatTracker();

const isDrawerOpen = ref(false);
const isMountComplete = ref(false);
const isAppReady = ref(false);
const nc = ref<any>(null);
const isInitialLoaded = ref(false);

let countdownTimer: any = null;
let appStateListener: any = null;

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value;
};

const closeDrawer = () => {
  isDrawerOpen.value = false;
};

const initializeData = async () => {
  if (isLoggedIn.value && user.value) {
    try {
      await settingsStore.fetchServicesSettings();
      await syncWorkspaceFromUrl();
      await getAttendanceStatus();

      const servers = import.meta.env.VITE_NATS_SERVER;
      const natsUser = import.meta.env.VITE_NATS_USER;
      const pass = import.meta.env.VITE_NATS_PASS;

      const connection = await connectNatsWithRetry(servers, natsUser, pass);
      nc.value = connection;
      setConnection(connection);

      const env = import.meta.env.VITE_ENV;
      const subject = `auth.${env}.${user.value.Id}`;
      natsSubscribe(nc.value, subject, activityActions);
      manageTracker(user.value.Id, user.value.TokenExp || null, true, false, false);
    } catch (err) {
      console.error('App: Initialization failed', err);
    }
  }
};

const syncWorkspaceFromUrl = async () => {
  const currentPath = window.location.pathname.replace(/^\//, '');
  if (currentPath && settingsStore.services?.[currentPath]) {
    serviceStore.selectService(settingsStore.services[currentPath]);
  }
};

const handleUserReturn = async () => {
  if (!isLoggedIn.value || !user.value || modalType.value === 'expired') {
    return;
  }
  if (showSessionModal.value) {
    await closeSessionModal();
  }
  manageTracker(user.value.Id, user.value.TokenExp, true, false, false);
};

const handleMessage = async (event: MessageEvent) => {
  if (!await validateOrigin(event.origin)) {
    return;
  }

  if (isValidHeilaMessage(event.data)) {
    const activeId = serviceStore.selected?.id || 'common';
    if (!await validateHandshakeNonce(event.data.nonce, activeId)) {
      console.warn(`[Security] Handshake nonce mismatch for service: ${activeId}. Possible replay or CSRF attack.`);
      return;
    }

    const { action, data } = event.data;
    await IframeBridge.handle(activeId, action, data);
  } else if (event.data?.type !== 'webpackOk') {
    console.warn('[Security] Received message with invalid schema', event.data);
  }
};

onBeforeMount(async () => {
  const shouldInitGeoPlugin = await StorageService.Get<boolean>("SHOULD_RUN_GEO_PLUGIN_ON_BEFORE_MOUNT");
  if (shouldInitGeoPlugin) {
    const geo = GeoLocationService.getInstance();
    geo.initialize()
        .then(() => console.log('initBackgroundGeolocation'))
        .catch(e => console.error("error on initBackgroundGeolocation", e));
  }
});

onMounted(async () => {
  SessionHub.registerTracker(handleUserReturn);

  await initLocale();
  const tokenFromUrl = getQueryParam('token');
  if (tokenFromUrl) {
    if (isNative) {
      await StorageService.Set("ACCESS_TOKEN", tokenFromUrl);
    } else {
      sessionStorage.setItem('iframe_active_token', tokenFromUrl);
    }
  }

  try {
    if (isNative) {
      const baseDomain = await getBaseDomain();
      if (baseDomain != '') {
        await settingsStore.fetchSuiteSettings();
      }
    } else {
      await settingsStore.fetchSuiteSettings();
    }
    await initLocale();
    if (isLoggedIn.value && user.value) {
      await initAuth();
    }
    markAuthReady();
    await setupNotifications(isLoggedIn, isAppReady);
  } finally {
    window.addEventListener('message', handleMessage);
    isMountComplete.value = true;
    isAppReady.value = true;
    await processPendingNotification(isLoggedIn.value);
  }

  appStateListener = await App.addListener('appStateChange', async ({ isActive }) => {
    if (isActive && isAppReady.value) {
      await processPendingNotification(isLoggedIn.value);
      SendIframe.onDeviceResume({});
    }
  });
});

watch(showSessionModal, async (isSessionOpen) => {
  if (isSessionOpen) {
    if (countdownTimer) clearInterval(countdownTimer);
    countdownTimer = setInterval(async () => {
      if (timeLeft.value > 0) {
        timeLeft.value--;
      } else {
        clearInterval(countdownTimer);
        modalType.value = 'expired';
      }
    }, 1000);

    USER_ACTIVITY_EVENTS.forEach(name => {
      document.addEventListener(name, handleUserReturn, { passive: true });
    });
  } else {
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
    USER_ACTIVITY_EVENTS.forEach(name => {
      document.removeEventListener(name, handleUserReturn);
    });
  }

  if (isSessionOpen) {
    await showModal({
      component: SessionModal,
      backdropDismiss: false,
      keyboardClose: false
    });
  }
});

watch(
    [() => route.params.serviceId as string, isLoggedIn, isAppReady],
    async ([serviceId, loggedIn, appReady]) => {
      if (!appReady) return;

      if (loggedIn) {
        await sendTokenIfReady();

        if (!isInitialLoaded.value) {
          isInitialLoaded.value = true;
          await initializeData();
        }
        manageTracker(user.value?.Id || null, user.value?.TokenExp || null, true, false, false);
        startPostMessageTracker();
      } else {
        isInitialLoaded.value = false;
        manageTracker(null, null, false, false, false);
        stopPostMessageTracker();

        await closeSessionModal();

        if (nc.value) {
          await nc.value.close();
          nc.value = null;
          setConnection(null);
        }
        if (isMountComplete.value) {
          isAppReady.value = true;
        }
      }
    },
    { immediate: true }
);

// Close the app drawer automatically whenever the user changes pages/routes
watch(
    () => route.path,
    () => {
      if (isDrawerOpen.value) {
        closeDrawer();
      }
    }
);

onUnmounted(() => {
  SessionHub.registerTracker(() => {});
  manageTracker(null, null, true, false, false);
  stopPostMessageTracker();
  window.removeEventListener('message', handleMessage);
  appStateListener?.remove();
  if (nc.value && !nc.value.isClosed()) {
    nc.value.close();
  }
});
</script>

<style scoped>
.loading-center {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background: #ffffff;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
}

.loader-content {
  text-align: center;
}

.loader-content p {
  margin-top: 15px;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.custom-spinner {
  --color: #1e3a8a !important;
  width: 32px;
  height: 32px;
  stroke-width: 4px;
}
</style>